import { useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCw, CloudUpload, CheckCircle2 } from "lucide-react";
import { useOfflineSync } from "@/lib/use-offline-sync";
import { cn } from "@/lib/utils";

/**
 * Compact connectivity + sync chip.
 * - Shows "Offline" when navigator is offline.
 * - Shows "Reconnecting" briefly when connectivity returns.
 * - Shows pending sync count and a manual sync action.
 * - Auto-hides when fully online with empty queue and no recent state change.
 */
export function OfflineStatusChip({ className }: { className?: string }) {
  const { online, pending, syncing, syncNow } = useOfflineSync();
  const [reconnected, setReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(!online);

  useEffect(() => {
    if (!online) {
      setWasOffline(true);
      setReconnected(false);
      return;
    }
    if (wasOffline) {
      setReconnected(true);
      setWasOffline(false);
      const t = window.setTimeout(() => setReconnected(false), 3000);
      return () => window.clearTimeout(t);
    }
  }, [online, wasOffline]);

  // Hide entirely when steady-state online with empty queue.
  if (online && pending === 0 && !syncing && !reconnected) return null;

  const Icon = !online ? WifiOff : syncing ? RefreshCw : reconnected ? Wifi : CloudUpload;

  const tone = !online
    ? "border-destructive/40 bg-destructive/10 text-destructive"
    : reconnected
      ? "border-success/40 bg-success/10 text-success"
      : "border-warning/40 bg-warning/10 text-warning";

  const label = !online
    ? "Offline — actions will queue"
    : syncing
      ? `Syncing ${pending} item${pending === 1 ? "" : "s"}…`
      : reconnected && pending === 0
        ? "Back online"
        : `${pending} pending sync`;

  const canSync = online && pending > 0 && !syncing;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
        tone,
        className,
      )}
    >
      <Icon className={cn("h-3.5 w-3.5", syncing && "animate-spin")} aria-hidden />
      <span className="truncate">{label}</span>
      {canSync && (
        <button
          type="button"
          onClick={() => void syncNow()}
          className="ml-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide underline-offset-2 hover:underline"
        >
          Sync now
        </button>
      )}
      {online && pending === 0 && reconnected && (
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
      )}
    </div>
  );
}
